import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { replaceImageUrl } from '../../utils/helpers/imageUtils';
import { useDarkMode } from '../../utils/hooks/useDarkMode';
import { useDeviceType } from '../../utils/hooks/useDeviceType';
import { DesktopHeader, MobileHeader } from './header';

/**
 * Header Section Component
 * Main header component that renders mobile and desktop versions
 * 
 * @param {Object} props
 * @param {Object} props.data - The header data
 * @param {String} props.pageUrl - Current page URL
 * @param {String} props.phoneNumber - Phone number for contact
 * @param {Boolean} props.isBookingInformation - Whether this is booking information page
 */
export default function HeaderSection({ data, pageUrl, phoneNumber, isBookingInformation = false }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isWindowScrolled, setIsWindowScrolled] = useState(false);
    const [isClient, setIsClient] = useState(false);
    const { isDarkMode } = useDarkMode();
    const deviceType = useDeviceType();
    const router = useRouter();

    const { url, alternativeText } = data.logo;
    const logoUrl = replaceImageUrl(url);

    const menu = data.Menu ?? [];
    const pagesUrls = data.hideMenu?.split(/\n/) ?? [];
    const foundHiddenPage = pagesUrls.find(url => url.trim() === pageUrl) || null;
    const isMenuVisible = foundHiddenPage ? false : true;

    useEffect(() => {
        // Set client-side flag
        setIsClient(true);
        
        const handleScroll = () => {
            setIsWindowScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
    const closeNav = () => setIsMenuOpen(false);
    
    // Handle navigation with proper timing
    const handleNavigation = (url) => {
        // Close menu after a small delay to allow navigation to start
        setTimeout(() => {
            setIsMenuOpen(false);
        }, 100);
    };

    // Check if current page should have black header initially
    const shouldHaveBlackHeader = useMemo(() => {
        const currentPath = router.asPath;
        return isWindowScrolled || 
               isBookingInformation || 
               currentPath.startsWith('/blogs') || 
               currentPath.startsWith('/author/');
    }, [isWindowScrolled, isBookingInformation, router.asPath]);

    // Memoized navbar style to prevent expensive calculations on every render
    const navbarStyle = useMemo(() => ({
        backgroundColor: shouldHaveBlackHeader 
            ? (isDarkMode ? '#1a1a1a' : '#16110D') 
            : 'transparent'
    }), [shouldHaveBlackHeader, isDarkMode]);

    const isMobile = isClient && deviceType === 'mobile';
    const isDesktop = isClient && deviceType === 'desktop';

    return (
        <>
            {isMobile && (
                <MobileHeader
                    data={data}
                    logoUrl={logoUrl}
                    alternativeText={alternativeText}
                    menu={menu}
                    isMenuVisible={isMenuVisible}
                    isMenuOpen={isMenuOpen}
                    toggleMenu={toggleMenu}
                    closeNav={closeNav}
                    handleNavigation={handleNavigation}
                    navbarStyle={navbarStyle}
                    isDarkMode={isDarkMode}
                />
            )}

            {isDesktop && (
                <DesktopHeader
                    data={data}
                    logoUrl={logoUrl}
                    alternativeText={alternativeText}
                    menu={menu}
                    isMenuVisible={isMenuVisible}
                    isMenuOpen={isMenuOpen}
                    setIsMenuOpen={setIsMenuOpen}
                    isBookingInformation={isBookingInformation}
                    navbarStyle={navbarStyle}
                    isDarkMode={isDarkMode}
                />
            )}
        </>
    );
}
